import { DeviceType } from '@/enums';
import { cdnAxios } from '@/services/axiosInstances';
import type { PoiContent, ScreenSaverInfo } from '@/services/dto';

/**
 * S3 Cache Service for direct S3 access
 * Fetches cached data directly from S3 using the cache structure created by cron jobs
 * Cache structure: {ENVIRONMENT}/cache/{type}/{date}/{filename}
 */
export class S3CacheService {
    /**
     * Get S3 cache path for axios request
     * @param cacheType - Cache type (store-data, poi-data, faq-data, screen-data)
     * @param date - Date in YYYY-MM-DD format
     * @param filename - File name (store_X.json or building_X.json)
     */
    private static getS3CachePath(cacheType: string, date: string, filename: string): string {
        const environment = import.meta.env.VITE_ENVIRONMENT || 'development';
        return `/${environment}/cache/${cacheType}/${date}/${filename}`;
    }

    /**
     * Fetch JSON data from S3 using axios
     */
    private static async fetchJsonFromS3<T = any>(path: string): Promise<any> {
        try {
            return await cdnAxios.get<T>(path);
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 403) {
                throw new Error(`S3 access forbidden (403). Check CDN permissions.`);
            }
            if (status === 404) {
                throw new Error(`S3 file not found (404): ${path}`);
            }

            throw error;
        }
    }

    /**
     * Get store config data directly from S3
     */
    static async getStoreConfigData(storeId: number, date?: string): Promise<any> {
        const targetDate = date || new Date().toISOString().split('T')[0];
        const filename = `store_${storeId}.json`;
        const path = this.getS3CachePath('store-data', targetDate, filename);

        return await this.fetchJsonFromS3(path);
    }

    /**
     * Get POI contents data directly from S3
     */
    static async getPoiContentsData(storeId: number, date?: string): Promise<any> {
        const targetDate = date || new Date().toISOString().split('T')[0];
        const filename = `store_${storeId}.json`;
        const path = this.getS3CachePath('poi-data', targetDate, filename);

        return await this.fetchJsonFromS3(path);
    }

    /**
     * Get screen saver data directly from S3
     */
    static async getScreenSaverData(buildingId: number, date?: string): Promise<any> {
        const targetDate = date || new Date().toISOString().split('T')[0];
        const filename = `building_${buildingId}.json`;
        const path = this.getS3CachePath('screen-data', targetDate, filename);

        return await this.fetchJsonFromS3(path);
    }

    /**
     * Get processed store config from cache
     */
    static async getProcessedStoreConfig(dataConfig: any): Promise<any> {
        try {
            // Get store data
            const cachedStoreConfig = await this.getStoreConfigFromCache(dataConfig.store.id);
            const buildingPoiContents = (await this.getPoiContentsFromCache(dataConfig.store.id)) || { byBuilding: {} };
            if (cachedStoreConfig) {
                // Add Store Config
                dataConfig.store = { ...dataConfig.store, ...cachedStoreConfig };

                // Add POI contents for each building from cache
                if (dataConfig.store.buildings) {
                    dataConfig.store.buildings = await Promise.all(
                        dataConfig.store.buildings.map(async (building: any) => {
                            return {
                                ...building,
                                poiContents: buildingPoiContents?.byBuilding[building.studioBuildingId],
                                contentsPropertyManagement: dataConfig.store.contentsPropertyManagement,
                            };
                        })
                    );
                }
            }

            // Get Screen Saver
            dataConfig.screenSaver = await this.getScreenSaverFromCache({
                buildingId: Number(dataConfig.building.id),
                buildingType: dataConfig.building.buildingType,
                floorId: Number(dataConfig.floor.id),
                orientation: dataConfig.machine.orientation,
                deviceType: dataConfig.machine?.deviceType,
            });

            return dataConfig;
        } catch (error) {
            console.error('Error processing store config from cache:', error);
            throw error;
        }
    }

    /**
     * Get screen saver from cache with filtering (like backend)
     */
    private static async getScreenSaverFromCache({
        buildingId,
        buildingType,
        floorId,
        orientation,
        deviceType,
    }: {
        buildingId: number;
        buildingType: string;
        floorId: number;
        orientation: string;
        deviceType?: DeviceType;
    }): Promise<ScreenSaverInfo[]> {
        try {
            const data = await this.getScreenSaverData(buildingId);
            const filteredData = this.filterScreenDataByOrientationAndFloor(data, orientation, buildingType, floorId, deviceType);
            return filteredData;
        } catch (error) {
            console.error('Error getting screen saver from cache:', error);
            return [];
        }
    }

    /**
     * Get store config from cache
     */
    private static async getStoreConfigFromCache(storeId: string): Promise<any> {
        try {
            const data = await this.getStoreConfigData(parseInt(storeId, 10));
            return data;
        } catch (error) {
            console.error('Error getting store config from cache:', error);
            return null;
        }
    }

    /**
     * Filter screen saver data by floor ID
     */
    private static filterByFloorId(screenSavers: any[], floorId: number | string | null): any[] {
        if (!screenSavers || !Array.isArray(screenSavers)) {
            return screenSavers;
        }

        if (floorId == null) {
            return screenSavers.filter((screenSaver) => {
                const screenFloorId = screenSaver.floorId;
                return screenFloorId === null || screenFloorId === undefined;
            });
        }

        return screenSavers.filter((screenSaver) => {
            const screenFloorId = screenSaver.floorId;
            return screenFloorId === null || screenFloorId === undefined || String(screenFloorId) === String(floorId);
        });
    }
    private static filterByBuildingType(screenSavers: any[], buildingType: string | null | undefined): any[] {
        if (!screenSavers || !Array.isArray(screenSavers)) {
            return screenSavers;
        }

        if (buildingType == null || buildingType === '') {
            return screenSavers.filter((item) => item.buildingType === null || item.buildingType === undefined);
        }

        return screenSavers.filter((item) => {
            const itemBt = item.buildingType;
            return itemBt === null || itemBt === undefined || String(itemBt) === String(buildingType);
        });
    }

    /**
     * Filter screen saver data by orientation, buildingType and floorId.
     * Signage devices use the dedicated `signage` bucket in CDN cache.
     */
    private static filterScreenDataByOrientationAndFloor(
        data: any,
        orientation: string,
        buildingType: string | null | undefined,
        floorId: number | string | null,
        deviceType?: DeviceType
    ): ScreenSaverInfo[] {
        const sourceData = deviceType === DeviceType.SIGNAGE ? data?.[DeviceType.SIGNAGE] : data;
        const orientationBucket = sourceData?.[orientation];

        let byBuildingType: any[] = [];
        if (Array.isArray(orientationBucket)) {
            byBuildingType = deviceType === DeviceType.SIGNAGE ? orientationBucket : this.filterByBuildingType(orientationBucket, buildingType);
        } else {
            const orientationData = orientationBucket || {};
            byBuildingType = orientationData[buildingType as string] || [];
        }

        return deviceType === DeviceType.SIGNAGE ? byBuildingType : this.filterByFloorId(byBuildingType, floorId);
    }

    /**
     * Get POI contents from cache
     */
    private static async getPoiContentsFromCache(storeId: string): Promise<{ byBuilding: Record<string, PoiContent[]> } | null> {
        try {
            return await this.getPoiContentsData(parseInt(storeId, 10));
        } catch (error) {
            console.error('Error getting POI contents from cache:', error);
            return null;
        }
    }
}
